<template>
    <main class="not-found-page">
        <div class="not-found-page__glow not-found-page__glow--one"></div>
        <div class="not-found-page__glow not-found-page__glow--two"></div>

        <section class="not-found" aria-labelledby="not-found-title">
            <div class="not-found__visual" aria-hidden="true">
                <span class="not-found__number">4</span>
                <div class="not-found__compass">
                    <i class="fa fa-compass"></i>
                </div>
                <span class="not-found__number">4</span>
            </div>

            <p class="not-found__eyebrow">SchoolDock / Navigation error</p>
            <h1 id="not-found-title">This page took a wrong turn.</h1>
            <p class="not-found__message">
                The address may be outdated, or the page may have moved. Let’s get you back to somewhere useful.
            </p>

            <div class="not-found__actions">
                <button type="button" class="not-found__button not-found__button--primary" 
                    @click="goHome">
                    <i class="fa fa-home" aria-hidden="true"></i>
                    <span>Go to home</span>
                </button>
                <button type="button" class="not-found__button not-found__button--quiet" @click="goBack">
                    <i class="fa fa-arrow-left" aria-hidden="true"></i>
                    <span>Go back</span>
                </button>
            </div>

            <p class="not-found__code">Error code <strong>404</strong></p>
        </section>
    </main>
</template>

<script>
export default {
    name: 'NotFound',    
    methods: {
        goHome() {
            this.$store.dispatch("auth/Logout").then(() => {
                console.log('User logged out');
                this.$router.push("/login");
            });
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.goHome();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.not-found-page {
    --ink: #172033;
    --muted: #68738a;
    --blue: #2458bd;
    --blue-dark: #173d8a;
    position: relative;
    min-height: calc(100vh - 72px);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    background: #f5f7fb;
    color: var(--ink);
}

.not-found-page::before {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(36, 88, 189, 0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(36, 88, 189, 0.045) 1px, transparent 1px);
    background-size: 42px 42px;
    content: '';
    mask-image: linear-gradient(to bottom, black, transparent 76%);
}

.not-found-page__glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(2px);
    opacity: 0.8;
}

.not-found-page__glow--one {
    top: -140px;
    right: 11%;
    width: 300px;
    height: 300px;
    background: rgba(107, 174, 255, 0.24);
}

.not-found-page__glow--two {
    bottom: -180px;
    left: 8%;
    width: 360px;
    height: 360px;
    background: rgba(255, 203, 92, 0.2);
}

.not-found {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 720px;
    text-align: center;
    animation: arrive 0.55s ease-out both;
}

.not-found__visual {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    margin-bottom: 24px;
    color: var(--blue);
    font-size: clamp(7rem, 22vw, 11rem);
    font-weight: 800;
    line-height: 0.8;
    letter-spacing: -0.06em;
}

.not-found__compass {
    display: grid;
    width: clamp(92px, 18vw, 132px);
    height: clamp(92px, 18vw, 132px);
    border: 10px solid var(--blue);
    border-radius: 50%;
    place-items: center;
    color: #f0b849;
    font-size: clamp(3.4rem, 9vw, 5rem);
    line-height: 1;
    transform: rotate(-18deg);
    box-shadow: 0 15px 30px rgba(36, 88, 189, 0.16);
}

.not-found__eyebrow {
    margin: 0 0 14px;
    color: var(--blue);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

h1 {
    margin: 0;
    font-size: clamp(2rem, 5vw, 3.65rem);
    font-weight: 800;
    line-height: 1.05;
}

.not-found__message {
    max-width: 500px;
    margin: 18px auto 0;
    color: var(--muted);
    font-size: 1.05rem;
    line-height: 1.7;
}

.not-found__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-top: 30px;
}

.not-found__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 48px;
    padding: 0 20px;
    border: 1px solid transparent;
    border-radius: 8px;
    font: inherit;
    font-size: 0.92rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.not-found__button:hover:not(:disabled) {
    transform: translateY(-2px);
}

.not-found__button:focus-visible {
    outline: 3px solid rgba(36, 88, 189, 0.3);
    outline-offset: 3px;
}

.not-found__button--primary {
    background: var(--blue);
    color: #fff;
    box-shadow: 0 8px 18px rgba(36, 88, 189, 0.22);
}

.not-found__button--primary:hover:not(:disabled) {
    background: var(--blue-dark);
    box-shadow: 0 12px 24px rgba(36, 88, 189, 0.28);
}

.not-found__button--quiet {
    border-color: #d7deeb;
    background: rgba(255, 255, 255, 0.7);
    color: var(--ink);
}

.not-found__button--quiet:hover:not(:disabled) {
    background: #fff;
}

.not-found__button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.not-found__code {
    margin: 34px 0 0;
    color: #9aa4b7;
    font-size: 0.78rem;
}

.not-found__code strong {
    color: #6e7a91;
}

@keyframes arrive {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 480px) {
    .not-found-page {
        min-height: 100vh;
        padding: 48px 18px;
    }

    .not-found__visual {
        gap: 8px;
    }

    .not-found__button {
        width: 100%;
    }
}
</style>
  